/*
  Lead tracking — one seam for every form that posts to the
  quote-request-public Supabase Edge Function (showroom Request Pricing, Text Us,
  the quote-request app bundle, ad landing pages).

  1. Attribution: on every page load, a Google click id (gclid / gbraid /
     wbraid) or utm_* in the URL is remembered in localStorage for 90 days,
     last ad click wins. A visitor who lands on an ad page and submits a form
     three pages later still carries the campaign.
  2. On a lead POST: the remembered click ids are added to the JSON body, and
     blank utm_* fields are filled in. Fields the form set itself are kept.
  3. On a successful response ({ ok: true }, HTTP 200): fires ONE GA4 event
     named by the lead's intent (quote_requests.intent, 2026-10-25):
       buy_spa   -> generate_lead     (hot tub / swim spa sale inquiry — the
                                       conversion Google Ads bids on)
       buy_sauna -> sauna_lead        (sauna sale inquiry — the Sauna
                                       campaign's conversion)
       service   -> service_request   (contact handling, never a lead)
       product   -> product_question  (parts / order, never a lead)
     with { form, showroom, lp, intent } — no personal data. The intent comes
     from the response first (the Edge Function derives one from `form` when
     a page sent none), then the body, then INTENT_BY_FORM here, so a cached
     page or an older function still lands on the right event. When the
     response carries a request_number (RQ-…) and the body a spa `model`, they
     ride along as request_number / spa_model.

  ONE emitter: this is the only lead-event emitter on any quote-request-public
  POST (one gtag call site, four event names). The quote-request bundle used to fire its own
  generate_lead { method, request_number, spa_model } on the same submit, so
  GA4 counted every quote-tool lead twice (CTO T098, 2026-10-11). Its call was
  removed from the bundle and its two useful params moved here. The bundle's
  source (Quote-Builder src/customer/CustomerApp.jsx) must lose that call too,
  or the next byte-copy brings the double count back —
  scripts/lead-tracking.test.mjs fails if it does.

  Deliberate corner: this wraps window.fetch instead of editing each form,
  because the quote-request bundle is built in another repo and must not be
  hand-edited. The wrapper touches ONLY string-bodied POSTs whose URL ends in
  /quote-request-public; anything else, and any error in here, goes straight
  to the original fetch. If the bundle ever stops using window.fetch, give it
  an explicit call to window.ArcticLead.track() instead.

  Honeypot: the Edge Function answers a filled hidden `website` field with a
  fake ok:true (so bots don't retry), so this script skips generate_lead when
  the body it sent had `website` filled — the response to bots stays identical.
  Reconcile GA4 against quote_requests (the source of truth) anyway —
  AI-Staff/CMO/PLAN-ads-consolidation.md.

  Payload contract: keep in sync with
  arctic-spas-ops-dashboard/supabase/functions/quote-request-public/index.ts
  (STRING_KEYS — unknown keys are rejected with 400 bad_keys).
*/
(function () {
  'use strict';

  var STORE_KEY = 'as_lead_attr';
  var TTL_MS = 90 * 24 * 60 * 60 * 1000; // Google's default click-through window
  var CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'];
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];
  var CLICK_ID_RE = /^[A-Za-z0-9_-]{1,200}$/;
  var ENDPOINT_RE = /\/quote-request-public\/?(\?|$)/;
  // Forms whose `city` is the showroom, not the customer's town.
  var SHOWROOM_FORM_RE = /^(showroom-pricing|lp-)/;
  // GA4 event per intent. Keep the values in step with the Edge Function's
  // INTENTS set and the GA4 key events (AI-Staff/CMO/PLAN-lead-intent-split.md).
  var EVENT_BY_INTENT = {
    buy_spa: 'generate_lead',
    buy_sauna: 'sauna_lead',
    service: 'service_request',
    product: 'product_question'
  };
  // Fallback when neither the response nor the body names an intent (an
  // older Edge Function). Mirrors the function's deriveIntent(): Text Us has
  // no safe default, a sauna form is a sauna sale, everything else a spa sale.
  function intentByForm(form) {
    if (form === 'text-us') return '';
    if (/sauna/.test(form || '')) return 'buy_sauna';
    return 'buy_spa';
  }

  function readStore() {
    try {
      var saved = JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null');
      if (!saved || !saved.ts || Date.now() - saved.ts > TTL_MS) return {};
      return saved;
    } catch (e) {
      return {};
    }
  }

  function capture() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var found = {};
    var any = false;
    CLICK_KEYS.forEach(function (k) {
      var v = params.get(k) || '';
      if (CLICK_ID_RE.test(v)) { found[k] = v; any = true; }
    });
    UTM_KEYS.forEach(function (k) {
      var v = (params.get(k) || '').slice(0, 120);
      if (v) { found[k] = v; any = true; }
    });
    if (!any) return;
    found.ts = Date.now();
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(found));
    } catch (e) { /* private window / blocked storage: attribution is best-effort */ }
  }

  function attribution() {
    var saved = readStore();
    var out = {};
    CLICK_KEYS.concat(UTM_KEYS).forEach(function (k) {
      if (saved[k]) out[k] = saved[k];
    });
    return out;
  }

  // request_number is minted server-side (RQ-YYYY-NNNNNN); spa_model is the
  // model name the visitor picked. Neither is personal data. Both are checked
  // here because they come from a response / form body, not from this script.
  var REQUEST_NUMBER_RE = /^RQ-[0-9]{4}-[0-9]{1,10}$/;

  /**
   * Fire the one GA4 lead event for a lead, named by its intent.
   * @param {string} form       the lead's `form` value (e.g. 'quote-request')
   * @param {string} [showroom] showroom name for showroom/lp forms
   * @param {{request_number?: unknown, spa_model?: unknown, intent?: unknown}} [extra]
   */
  function track(form, showroom, extra) {
    if (typeof window.gtag !== 'function') return;
    var intent = extra && typeof extra.intent === 'string' && EVENT_BY_INTENT[extra.intent]
      ? extra.intent
      : intentByForm(form);
    // No intent at all (a Text Us note through an old function): count it as
    // a question, never as the Ads conversion.
    var eventName = EVENT_BY_INTENT[intent] || 'product_question';
    var params = {
      form: form || 'unknown',
      showroom: showroom || '',
      lp: window.location.pathname,
      intent: intent || 'unknown'
    };
    if (extra) {
      if (typeof extra.request_number === 'string' && REQUEST_NUMBER_RE.test(extra.request_number)) {
        params.request_number = extra.request_number;
      }
      if (typeof extra.spa_model === 'string' && extra.spa_model.trim() !== '') {
        params.spa_model = extra.spa_model.trim().slice(0, 100);
      }
    }
    window.gtag('event', eventName, params);
  }

  function enrich(bodyText) {
    var payload = JSON.parse(bodyText);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    var attr = attribution();
    CLICK_KEYS.concat(UTM_KEYS).forEach(function (k) {
      if (!payload[k] && attr[k]) payload[k] = attr[k];
    });
    return payload;
  }

  function withoutClickIds(payload) {
    var copy = {};
    Object.keys(payload).forEach(function (k) {
      if (CLICK_KEYS.indexOf(k) === -1) copy[k] = payload[k];
    });
    return copy;
  }

  function isLeadPost(input, init) {
    if (!init || typeof init.body !== 'string') return false;
    if (String(init.method || 'GET').toUpperCase() !== 'POST') return false;
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    return ENDPOINT_RE.test(url);
  }

  function reinit(init, payload) {
    var next = {};
    Object.keys(init).forEach(function (k) { next[k] = init[k]; });
    next.body = JSON.stringify(payload);
    return next;
  }

  function install() {
    if (typeof window.fetch !== 'function' || window.fetch.__arcticLead) return;
    var nativeFetch = window.fetch;

    var wrapped = function (input, init) {
      var payload = null;
      try {
        if (isLeadPost(input, init)) payload = enrich(init.body);
      } catch (e) {
        payload = null;
      }
      if (!payload) return nativeFetch.apply(this, arguments);

      var self = this;
      return nativeFetch.call(self, input, reinit(init, payload)).then(function (res) {
        if (res.status !== 400) return res;
        // An Edge Function that predates the click-id keys rejects them with
        // bad_keys. Never lose the lead to a deploy-order slip: resend once
        // without them.
        return res.clone().json().catch(function () { return {}; }).then(function (data) {
          var keys = (data && data.error === 'bad_keys' && data.keys) || [];
          var ours = keys.length > 0 && keys.every(function (k) { return CLICK_KEYS.indexOf(k) !== -1; });
          if (!ours) return res;
          return nativeFetch.call(self, input, reinit(init, withoutClickIds(payload)));
        });
      }).then(function (res) {
        if (res.status === 200) {
          res.clone().json().then(function (data) {
            if (!data || !data.ok) return;
            // Honeypot hit: the ok:true is fake (no lead row). Same test as
            // quote-request-public's HONEYPOT_KEY check.
            var hp = payload.website;
            if (typeof hp === 'string' && hp.trim() !== '') return;
            var form = payload.form || 'quote-request';
            track(form, SHOWROOM_FORM_RE.test(form) ? payload.city : '', {
              request_number: data.request_number,
              spa_model: payload.model,
              intent: typeof data.intent === 'string' ? data.intent : payload.intent
            });
          }).catch(function () { /* not JSON: not a lead response */ });
        }
        return res;
      });
    };
    wrapped.__arcticLead = true;
    window.fetch = wrapped;
  }

  capture();
  install();
  window.ArcticLead = { attribution: attribution, track: track };
})();
